import { biCardSubtitle } from '@euler/app/components/message/widgets/bi-visualization/functions/biCardSubtitle';
import { biVizIcon } from '@euler/app/components/message/widgets/bi-visualization/functions/biVizIcon';
import { Typography } from '@euler/app/components/typography';
import { aiColors } from '@euler/app/flows/ai-suite/theme';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { BiCardView } from '@euler/model/bi/dashboard';
import { BiPalette, BiTitleAlign } from '@euler/model/bi/viz';
import { memo, useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { BiCardKind, biCardKind } from '../../functions/biCardKind';
import { biCardFits } from '../../functions/biGridLayout';
import { biVizInPalette } from '../../functions/biVizInPalette';
import { CardBody } from '../card/CardBody';
import { CardFrame } from '../card/CardFrame';
import { BiCardLoaded, BiCardResults } from '../functions/biCardResults';

/** The cards a preview draws, when the dashboard has them. */
const kPreviewCards = 6;

const noop = () => undefined;

/** What the preview is: its caption, and a phone's header on its page. */
export const kBiExportPreviewNote = '用已加载的图表预览，PDF 中的配色与此一致';

/**
 * 效果预览: the dashboard's cards as the export draws them in the picked
 * palette, from the data the dashboard already loaded — nothing is read
 * again. Up to six cards: one of each kind first (KPI group, bar, line,
 * pie, funnel…, and one table), the palette landing differently on each;
 * then the rest in reading order, never a second table. Captioned beside
 * the options on a wide screen; bare, the cards alone, as a phone's page
 * (the sheet's header says what it is).
 */
export const ExportPreview = memo(
  ({
    cards,
    order,
    results,
    palette,
    dashboardPalette,
    bare = false,
    clearance = 0,
  }: {
    cards: BiCardView[];
    /** The cards in reading order. */
    order: string[];
    results: BiCardResults;
    palette: BiPalette;
    dashboardPalette: BiPalette;
    /** No pane or caption: the cards on the sheet itself. */
    bare?: boolean;
    /** Room left under the last card, e.g. for buttons floating over it. */
    clearance?: number;
  }) => {
    const { rpx } = useResponsiveContext();
    const [loaded, setLoaded] = useState(results.results$.value);
    useEffect(() => {
      const subscription = results.results$.subscribe(setLoaded);
      return () => subscription.unsubscribe();
    }, [results]);
    const [paneWidth, setPaneWidth] = useState(0);

    const shown = useMemo(() => {
      const byId = new Map(cards.map(card => [card.id, card]));
      const candidates: { card: BiCardView; data: BiCardLoaded }[] = [];
      for (const id of order) {
        const card = byId.get(id);
        const data = loaded.get(id);
        if (!card || !data?.result.current.rows.length) continue;
        candidates.push({ card, data });
      }
      const kinds = new Set<string>();
      const firsts = candidates.filter(({ card }) => {
        if (kinds.has(card.viz.type)) return false;
        kinds.add(card.viz.type);
        return true;
      });
      const extra = candidates.filter(
        entry =>
          !firsts.includes(entry) &&
          biCardKind(entry.card.viz) !== BiCardKind.Table,
      );
      const picked = new Set([...firsts, ...extra].slice(0, kPreviewCards));
      return candidates.filter(entry => picked.has(entry));
    }, [cards, order, loaded]);

    const chartHeight = rpx(210);
    // The pane's inset sits inside its scroll view, so the scrollbar runs
    // at the pane's edge, clear of the cards.
    const inset = rpx(24);
    const contentWidth = paneWidth - inset * 2;
    // Two columns once the pane is wide enough for a chart in each.
    const columns = contentWidth >= rpx(560) ? 2 : 1;
    const gap = rpx(10);
    const cardWidth =
      columns === 2 ? Math.floor((contentWidth - gap) / 2) : undefined;

    const drawn = shown.map(({ card, data }) => {
      const viz = biVizInPalette(card.viz, palette, dashboardPalette);
      const fits = biCardFits(viz);
      return (
        <View
          key={card.id}
          // The cards only show the colors: no tooltips or taps, and a drag
          // over them scrolls the preview.
          pointerEvents="none"
          style={{ width: cardWidth ?? '100%' }}
        >
          <CardFrame
            icon={biVizIcon(viz.type)}
            title={card.title}
            titleHidden={viz.style?.title?.show === false}
            titleCentered={viz.style?.title?.align === BiTitleAlign.Center}
            subtitle={biCardSubtitle(viz, data.result.current)}
            height={fits ? undefined : chartHeight}
          >
            <CardBody
              state={{
                kind: 'ready',
                result: data.result,
                dateFilter: data.dateFilter,
              }}
              spec={card.spec}
              viz={viz}
              kind={biCardKind(viz)}
              palette={palette}
              scroll={!fits}
              onRetry={noop}
            />
          </CardFrame>
        </View>
      );
    });

    const empty = '看板上的图表加载后，可在这里预览配色';
    const cardsView = (
      <ScrollView
        style={{ flex: 1 }}
        onLayout={e => setPaneWidth(Math.round(e.nativeEvent.layout.width))}
        contentContainerStyle={{
          flexDirection: 'row',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap,
          padding: inset,
          paddingTop: bare ? rpx(4) : 0,
          paddingBottom: inset + clearance,
        }}
      >
        {!paneWidth ? null : shown.length || !bare ? (
          drawn
        ) : (
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            {empty}
          </Typography.Text>
        )}
      </ScrollView>
    );
    if (bare) return cardsView;

    const caption = (
      // Set as the options' heading beside it, on the same line.
      <View style={{ gap: rpx(4) }}>
        <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
          效果预览
        </Typography.Text>
        <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
          {shown.length ? `${kBiExportPreviewNote}。` : `${empty}。`}
        </Typography.Text>
      </View>
    );

    return (
      <View
        style={{
          flex: 1,
          // As far from the cards as the options' heading is from the sizes.
          gap: rpx(14),
        }}
      >
        <View style={{ paddingHorizontal: inset }}>{caption}</View>
        {cardsView}
      </View>
    );
  },
);
